import type { AdminAccount, AdminStatus, BidHistory, LiveEvent, Product, ProductMode } from "../types";

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: "include",
    ...init,
    headers: {
      ...(init?.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({ error: "Request failed" }));
    throw new Error(payload.error ?? "Request failed");
  }

  return response.json() as Promise<T>;
}

export function getProducts(): Promise<{ products: Product[] }> {
  return request("/api/products");
}

export function getProduct(productId: string): Promise<{ product: Product }> {
  return request(`/api/products/${productId}`);
}

export function createProduct(formData: FormData): Promise<{ product: Product }> {
  return request("/api/products", {
    method: "POST",
    body: formData,
  });
}

export function updateProduct(productId: string, formData: FormData): Promise<{ product: Product }> {
  return request(`/api/products/${productId}`, {
    method: "PATCH",
    body: formData,
  });
}

export function reserveProduct(
  productId: string,
  values: { employeeName: string; phone: string },
): Promise<{ product: Product }> {
  return request(`/api/products/${productId}/reserve`, {
    method: "POST",
    body: JSON.stringify(values),
  });
}

export function bidProduct(
  productId: string,
  values: { bidderName: string; phone: string; amount: number },
): Promise<{ product: Product }> {
  return request(`/api/products/${productId}/bid`, {
    method: "POST",
    body: JSON.stringify(values),
  });
}

export function setProductMode(productId: string, mode: ProductMode): Promise<{ product: Product }> {
  return request(`/api/products/${productId}/mode`, {
    method: "POST",
    body: JSON.stringify({ mode }),
  });
}

export function resetProduct(productId: string): Promise<{ product: Product }> {
  return request(`/api/products/${productId}/reset`, {
    method: "POST",
  });
}

export function deleteProduct(productId: string): Promise<{ ok: true }> {
  return request(`/api/products/${productId}`, {
    method: "DELETE",
  });
}

export function loginAdmin(username: string, password: string): Promise<AdminStatus> {
  return request("/api/admin/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
}

export function logoutAdmin(): Promise<AdminStatus> {
  return request("/api/admin/logout", {
    method: "POST",
  });
}

export function getAdminStatus(): Promise<AdminStatus> {
  return request("/api/admin/me");
}

export function getEvents(since?: number): Promise<{ events: LiveEvent[] }> {
  const query = typeof since === "number" ? `?since=${since}` : "";
  return request(`/api/events${query}`);
}

export function getAdminEvents(): Promise<{ events: LiveEvent[] }> {
  return request("/api/admin/events");
}

export function getAdmins(): Promise<{ admins: AdminAccount[] }> {
  return request("/api/admin/admins");
}

export function createAdmin(values: { username: string; password: string }): Promise<{ admin: AdminAccount }> {
  return request("/api/admin/admins", {
    method: "POST",
    body: JSON.stringify(values),
  });
}

export function updateAdminPassword(adminId: string, password: string): Promise<{ admin: AdminAccount }> {
  return request(`/api/admin/admins/${adminId}`, {
    method: "PATCH",
    body: JSON.stringify({ password }),
  });
}

export function getProductBids(productId: string): Promise<{ bids: BidHistory[] }> {
  return request(`/api/products/${productId}/bids`);
}
